// EstimationComparableForm.jsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { supabase } from "../lib/supabaseClient";

const initialState = {
  source_type: "",
  title: "",
  artist: "",
  estimated_value: "",
  source: "",
  medium: "",
  dimensions: { height: "", width: "", depth: "" },
  comment: "",
};

export default function EstimationComparableForm({ onSubmit, onCancel, artworkId }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(initialState);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (["height", "width", "depth"].includes(name)) {
      setFormData((previous) => ({
        ...previous,
        dimensions: { ...previous.dimensions, [name]: value },
      }));
    } else {
      setFormData((previous) => ({ ...previous, [name]: value }));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!artworkId) {
      setError(t("estimationComparableForm.errors.missingArtwork"));
      return;
    }

    setSaving(true);
    setError("");
    const { error: insertError } = await supabase.from("realized_sales").insert([
      {
        source_type: formData.source_type,
        title: formData.title,
        artist: formData.artist,
        realized_price: formData.estimated_value,
        source: formData.source,
        medium: formData.medium,
        dimensions: formData.dimensions,
        comment: formData.comment,
        artwork_id: artworkId,
      },
    ]);

    setSaving(false);
    if (insertError) {
      console.error("[EstimationComparableForm] insert error:", insertError);
      setError(t("estimationComparableForm.errors.insert", { message: insertError.message }));
      return;
    }

    toast.success(t("estimationComparableForm.success"));
    onSubmit?.(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[80vh] overflow-y-auto p-4">
      <h2 className="text-xl font-semibold">{t("estimationComparableForm.title")}</h2>

      <div>
        <label>{t("estimationComparableForm.fields.sourceType.label")}</label>
        <input
          name="source_type"
          value={formData.source_type}
          onChange={handleChange}
          placeholder={t("estimationComparableForm.fields.sourceType.placeholder")}
          className="w-full border p-2"
        />
      </div>

      <div>
        <label>{t("estimationComparableForm.fields.title")}</label>
        <input name="title" value={formData.title} onChange={handleChange} className="w-full border p-2" />
      </div>

      <div>
        <label>{t("estimationComparableForm.fields.artist")}</label>
        <input name="artist" value={formData.artist} onChange={handleChange} className="w-full border p-2" />
      </div>

      <div>
        <label>{t("estimationComparableForm.fields.estimatedValue")}</label>
        <input name="estimated_value" type="number" value={formData.estimated_value} onChange={handleChange} className="w-full border p-2" />
      </div>

      <div>
        <label>{t("estimationComparableForm.fields.exactSource")}</label>
        <input name="source" value={formData.source} onChange={handleChange} className="w-full border p-2" />
      </div>

      <div>
        <label>{t("estimationComparableForm.fields.medium")}</label>
        <input name="medium" value={formData.medium} onChange={handleChange} className="w-full border p-2" />
      </div>

      <div>
        <label>{t("estimationComparableForm.fields.dimensions.label")}</label>
        <div className="grid grid-cols-3 gap-2">
          <input type="text" name="height" placeholder={t("estimationComparableForm.fields.dimensions.height")} value={formData.dimensions.height} onChange={handleChange} className="w-full border p-2" />
          <input type="text" name="width" placeholder={t("estimationComparableForm.fields.dimensions.width")} value={formData.dimensions.width} onChange={handleChange} className="w-full border p-2" />
          <input type="text" name="depth" placeholder={t("estimationComparableForm.fields.dimensions.depth")} value={formData.dimensions.depth} onChange={handleChange} className="w-full border p-2" />
        </div>
      </div>

      <div>
        <label>{t("estimationComparableForm.fields.comment")}</label>
        <textarea name="comment" value={formData.comment} onChange={handleChange} className="w-full border p-2" />
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex justify-end gap-2 pt-4">
        <button type="button" onClick={onCancel} className="btn-artsy btn-secondary-artsy">
          {t("estimationComparableForm.cancel")}
        </button>
        <button type="submit" className="btn-artsy btn-primary-artsy" disabled={saving}>
          {saving ? t("estimationComparableForm.saving") : t("estimationComparableForm.add")}
        </button>
      </div>
    </form>
  );
}
